/**
 * Nomination Bracelet Cart Validation
 *
 * This script enforces the rule that Nomination bracelets must be purchased with
 * at least one Nomination charm per bracelet.
 *
 * Rules:
 * - 1:1 ratio - each Nomination bracelet needs at least one Nomination charm
 * - Only Nomination-branded charms count toward the ratio
 * - Charms (any brand) can still be purchased standalone
 * - Only applies when Nomination bracelets are in cart
 * - Blocks checkout when rule is violated
 *
 * Neil & Barker product taxonomy
 * ------------------------------
 * This catalogue was imported from VisualSoft and does NOT carry the
 * "NOMINATION" / "BRACELETS" / "CHARMS" tag trio used on other Autify builds.
 * Here the brand lives on the product *vendor* and the range on the product
 * *type*:
 *
 *   Nomination bracelet -> vendor "Nomination" + type "Bracelets"
 *   Nomination charm     -> vendor "Nomination" + type "Charms" / "Composable Link"
 *
 * Identification is therefore driven by the `matchers` config below, which reads
 * vendor, type and tags off the cart row (see `data-product-vendor`,
 * `data-product-type` and `data-product-tags` in sections/main-cart-items.liquid
 * and snippets/cart-drawer.liquid). Tag-based matching is retained as a fallback
 * so the rule keeps working if merchandising later tags products explicitly.
 */

class NominationCartValidation {
  constructor() {
    // Legacy tag names - still honoured as a fallback (see matchers below)
    this.nominationBraceletTag = 'NOMINATION';
    this.braceletTag = 'BRACELETS';
    this.charmsTag = 'CHARMS';

    /**
     * How a cart line is classified. All values are compared upper-cased and
     * trimmed, so casing in the Shopify admin does not matter.
     *
     * The vendor, bracelet and charm values come from the theme editor
     * ("Theme settings -> Nomination cart validation"), passed in from
     * layout/theme.liquid as window.nominationMatchers. The defaults below apply
     * when a setting is blank, so the rule keeps working on a store that has
     * never been configured - and on a theme whose layout does not set the global
     * at all, which is what makes this script safe to drop into another build.
     */
    const configured = window.nominationMatchers || {};

    this.matchers = {
      // A line must match the brand before it can be a bracelet or a charm
      brandVendors: this.parseList(configured.vendor, ['NOMINATION']),
      // Fallback for tag-based catalogues. NOMINATION stays regardless of the
      // configured vendor so legacy tagged products keep matching.
      brandTags: [...new Set([...this.parseList(configured.vendor, []), 'NOMINATION'])],
      // Bracelets (the composable base bracelets). Matched as a substring of the
      // product type, so singular/plural and any casing ("Bracelet", "Bracelets",
      // "bracelets") all match. See isNominationBracelet for why tags are only a
      // fallback here.
      braceletKeywords: this.parseKeywords(configured.braceletKeywords, ['BRACELET']),
      // Charms / composable links that satisfy the ratio. Matched as a substring
      // of the product type or any tag.
      charmKeywords: this.parseKeywords(configured.charmTypes, ['CHARM', 'COMPOSABLE LINK']),
    };

    // Where the cart warning sends customers to browse charms. Set in the theme
    // editor under "Theme settings -> Nomination cart validation" and passed in
    // via layout/theme.liquid; the fallback keeps the link working if unset.
    this.charmUrl = window.nominationCharmUrl || '/collections/nomination-charms';

    this.warningMessage = `Nomination bracelets must be purchased with at least one Nomination charm per bracelet. Please add <a href="${this.escapeHtml(
      this.charmUrl,
    )}" style="color: #721c24; text-decoration: underline; font-weight: bold;">a Nomination charm</a> to continue.`;
    this.debug = false; // Set to true for debugging
    this.showDebugTags = false; // Set to true to show tag badges on cart items (for testing only)

    // Add debounce for cart refresh to prevent infinite loops
    this.lastCartRefresh = 0;
    this.cartRefreshDebounce = 2000; // 2 seconds debounce to prevent conflicts
    this.lastValidation = 0;
    this.validationDebounce = 800; // 800ms debounce for validation to prevent conflicts
    this.isValidating = false; // Flag to prevent conflicts with other scripts

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupValidation());
    } else {
      this.setupValidation();
    }
  }

  setupValidation() {
    // Listen for cart updates
    this.listenForCartUpdates();

    // Initial validation check
    this.validateCart();

    // Remove continuous validation - only validate on specific events
    this.validationInterval = null;
    this.removeValidationInterval = null;

    // Validate when page becomes visible (user comes back to tab)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        setTimeout(() => this.validateCart(), 100);
      }
    });

    // Validate when window regains focus
    window.addEventListener('focus', () => {
      setTimeout(() => this.validateCart(), 100);
    });
  }

  listenForCartUpdates() {
    // Wait for Cart Manager to be available
    if (!window.CartManager) {
      setTimeout(() => this.listenForCartUpdates(), 100);
      return;
    }

    // Use Cart Manager for all cart events
    this.cartManager = window.CartManager;

    // Listen for cart updates
    this.unsubscribeCartUpdated = this.cartManager.on(
      'cart:updated',
      () => {
        // Delay validation to avoid conflicts with engraving feature
        setTimeout(() => this.validateCart(true), 100);
      },
      this,
    );

    // Listen for cart API calls
    this.unsubscribeCartApiCall = this.cartManager.on(
      'cart:api-call',
      (data) => {
        setTimeout(() => this.validateCart(), 20);
      },
      this,
    );

    // Listen for quantity changes
    this.unsubscribeQuantityChange = this.cartManager.on(
      'cart:quantity-change',
      (data) => {
        // Delay validation to allow DOM to update and avoid conflicts
        setTimeout(() => this.validateCart(true), 50);
      },
      this,
    );

    // Listen for item removal
    this.unsubscribeItemRemove = this.cartManager.on(
      'cart:item-remove',
      (data) => {
        setTimeout(() => this.forceCartRefresh(), 1500);
      },
      this,
    );

    // Listen for DOM changes
    this.unsubscribeDomChanged = this.cartManager.on(
      'cart:dom-changed',
      () => {
        this.validateCart();
      },
      this,
    );

    // Listen for form submissions
    this.unsubscribeFormSubmit = this.cartManager.on(
      'cart:form-submit',
      (data) => {
        this.validateCart();
      },
      this,
    );

    // Prevent checkout when validation fails
    document.addEventListener('click', (event) => {
      const checkoutButton = event.target.closest(
        'button[name="checkout"], #checkout, .cart__checkout-button, button[form="cart"]',
      );
      if (checkoutButton && checkoutButton.hasAttribute('data-nomination-disabled')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    });

    // Prevent form submission when checkout is disabled
    document.addEventListener('submit', (event) => {
      const checkoutButton = event.target.querySelector(
        'button[name="checkout"], #checkout, .cart__checkout-button, button[form="cart"]',
      );
      if (checkoutButton && checkoutButton.hasAttribute('data-nomination-disabled')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    });
  }

  validateCart(forceValidation = false) {
    // Check for conflicts with other cart systems
    if (window.engravingFeature?.state?.isProcessing || window.engravingFeature?.isEnforcingRules) {
      return;
    }

    // Add debouncing to prevent excessive validation calls, but allow forced validation
    const now = Date.now();
    if (!forceValidation && now - this.lastValidation < this.validationDebounce) {
      return;
    }
    this.lastValidation = now;

    // Set validating flag to prevent conflicts
    if (this.isValidating) {
      return;
    }
    this.isValidating = true;

    try {
      const cartItems = this.getCartItems();

      // Add stability check - ensure we have valid cart items
      if (!cartItems || cartItems.length === 0) {
        this.clearAllWarnings();
        this.enableCheckout();
        return;
      }

      const nominationBracelets = this.getNominationBracelets(cartItems);
      const charms = this.getCharms(cartItems);

      // Debug: Display tags on cart items (only if enabled)
      if (this.showDebugTags) {
        this.displayTagsOnCartItems(cartItems);
      }

      // Debug logging for cart drawer (temporary - remove after fixing)
      if (this.debug && this.isCartDrawerOpen()) {
        console.log('=== Cart Drawer Validation Debug ===');
        console.log('Total cart items:', cartItems.length);
        console.log(
          'Nomination bracelets found:',
          nominationBracelets.length,
          nominationBracelets.map((b) => b.title),
        );
        console.log(
          'Charms found:',
          charms.length,
          charms.map((c) => c.title),
        );
        cartItems.forEach((item) => {
          const tags = this.getProductTagsFromElement(item.element);
          console.log(`Item: ${item.title}, Tags:`, tags);
        });
      }

      // Only validate if there are Nomination bracelets in cart
      if (nominationBracelets.length === 0) {
        this.clearAllWarnings();
        this.enableCheckout();
        return;
      }

      // Check if we have enough charms (1:1 ratio)
      const totalBraceletQuantity = this.getTotalBraceletQuantity(nominationBracelets);
      const totalCharmQuantity = this.getTotalCharmQuantity(charms);

      // Check if we have enough charms for all bracelets (1:1 ratio)
      if (totalCharmQuantity < totalBraceletQuantity) {
        // Idempotent - safe to call on every pass, and re-creates the warning if
        // the cart section was re-rendered underneath us.
        this.showWarnings(nominationBracelets, totalBraceletQuantity, totalCharmQuantity);
        this.disableCheckout();
      } else {
        this.forceClearWarnings();
        this.enableCheckout();
      }
    } catch (error) {
      console.error('Error during validation:', error);
      // If there's an error but warnings should be shown, try to maintain them
      if (this.warningsShouldBeShown) {
        setTimeout(() => this.validateCart(true), 100);
      }
    } finally {
      // Clear validating flag
      this.isValidating = false;
    }
  }

  getCartItems() {
    // Use Cart Manager if available, otherwise fallback to DOM parsing
    if (this.cartManager) {
      return this.cartManager.getCartItemsFromDOM();
    }

    // Fallback to original DOM parsing
    const cartItems = [];

    // Get items from main cart page
    const mainCartItems = document.querySelectorAll('#main-cart-items .cart-item');

    mainCartItems.forEach((item, index) => {
      const productTitle = item.querySelector('.cart-item__name')?.textContent?.trim();
      const quantity = parseInt(item.querySelector('input[name="updates[]"]')?.value || 0);
      const variantId = item.querySelector('input[name="updates[]"]')?.dataset?.quantityVariantId;
      const itemKey = item.getAttribute('data-cart-item-key') || item.getAttribute('data-index') || `main-${index}`;

      if (productTitle && quantity > 0) {
        cartItems.push({
          title: productTitle,
          quantity: quantity,
          variantId: variantId,
          itemKey: itemKey,
          element: item,
          isDrawer: false,
        });
      }
    });

    // Get items from cart drawer
    const drawerItems = document.querySelectorAll('#CartDrawer-CartItems .cart-item');
    drawerItems.forEach((item, index) => {
      const productTitle = item.querySelector('.cart-item__name')?.textContent?.trim();
      const quantity = parseInt(item.querySelector('input[name="updates[]"]')?.value || 0);
      const variantId = item.querySelector('input[name="updates[]"]')?.dataset?.quantityVariantId;
      const itemKey = item.getAttribute('data-cart-item-key') || item.getAttribute('data-index') || index;

      if (productTitle && quantity > 0) {
        // Use unique cart item key to identify each line item separately
        // This ensures products with same title but different properties (e.g., engraving) are counted separately
        const existingItem = cartItems.find((cartItem) => cartItem.itemKey === itemKey);

        if (!existingItem) {
          cartItems.push({
            title: productTitle,
            quantity: quantity,
            variantId: variantId,
            itemKey: itemKey,
            element: item,
            isDrawer: true,
          });
        }
      }
    });

    return cartItems;
  }

  getNominationBracelets(cartItems) {
    return cartItems.filter((item) => this.isNominationBracelet(item.element));
  }

  getCharms(cartItems) {
    return cartItems.filter((item) => this.isNominationCharm(item.element));
  }

  /**
   * A Nomination bracelet: Nomination brand + a bracelet keyword on the product
   * TYPE. Matched case-insensitively as a substring, so "Bracelet", "Bracelets",
   * "bracelets" and "Composable Bracelet" all qualify.
   *
   * The product type is authoritative whenever it is set, and tags are consulted
   * only when it is absent (a legacy tag-only catalogue). Letting a *tag* promote
   * a line to "bracelet" is not safe: an ordinary merchandising tag such as
   * "Charms for Bracelets" would reclassify a charm as a bracelet AND drop it from
   * the charm count, blocking checkout on a charms-only cart. To correct a
   * mis-typed product, set its product type rather than adding a tag.
   */
  isNominationBracelet(element) {
    if (!this.isNominationBrand(element)) return false;

    const type = this.getProductTypeFromElement(element);
    if (type) return this.matchesKeyword(this.matchers.braceletKeywords, type);

    return this.getNormalisedTags(element).some((tag) => this.matchesKeyword(this.matchers.braceletKeywords, tag));
  }

  /**
   * A Nomination charm: Nomination brand + a charm keyword on the product type or
   * any tag, and not itself a bracelet.
   *
   * Matched as a substring, symmetrically with bracelets: an exact-only test
   * refuses near-miss types such as "Composable Charms" and blocks a cart that
   * does contain a charm. Tags stay in play here (unlike bracelets) because
   * classifying *more* things as charms only ever unblocks a cart, so a stray tag
   * cannot cost a sale.
   */
  isNominationCharm(element) {
    if (!this.isNominationBrand(element)) return false;
    if (this.isNominationBracelet(element)) return false;

    const type = this.getProductTypeFromElement(element);
    const tags = this.getNormalisedTags(element);

    return (
      this.matchesKeyword(this.matchers.charmKeywords, type) ||
      tags.some((tag) => this.matchesKeyword(this.matchers.charmKeywords, tag))
    );
  }

  /** True when `value` contains any of `keywords`. Both are already upper-cased. */
  matchesKeyword(keywords, value) {
    if (!value) return false;
    return keywords.some((keyword) => value.includes(keyword));
  }

  /**
   * Brand check.
   *
   * The vendor is authoritative whenever the attribute carries a value: a tag is
   * only a fallback for catalogues that expose no vendor at all. Treating the tag
   * as an alternative would let a competitor's product tagged "Nomination" (a
   * common "shop by brand" or cross-sell tag) satisfy the rule, which the
   * requirement explicitly rules out.
   */
  isNominationBrand(element) {
    const vendor = this.getProductVendorFromElement(element);
    if (vendor) return this.matchers.brandVendors.includes(vendor);

    const tags = this.getNormalisedTags(element);
    return this.matchers.brandTags.some((tag) => tags.includes(tag));
  }

  getProductTagsFromElement(element) {
    // Use Cart Manager if available, otherwise fallback to direct DOM access
    if (this.cartManager) {
      return this.cartManager.getProductTagsFromElement(element);
    }

    // Fallback to original implementation
    if (!element) return [];

    const tagsString = element.getAttribute('data-product-tags');

    if (tagsString) {
      const tags = tagsString.split(',').map((tag) => tag.trim());
      return tags;
    }

    return [];
  }

  /**
   * Tags upper-cased so admin casing ("Composable Link") does not matter.
   */
  getNormalisedTags(element) {
    return this.getProductTagsFromElement(element).map((tag) => tag.trim().toUpperCase());
  }

  /**
   * Turn a theme setting into a normalised match list.
   *
   * Accepts a comma-separated string ("Charms, Composable Link") or an array, and
   * returns trimmed upper-case entries with blanks dropped. Falls back to
   * `fallback` when the setting is missing or contains nothing usable, so a
   * merchant cannot switch the rule off by clearing a field.
   */
  parseList(value, fallback) {
    const raw = Array.isArray(value) ? value : String(value ?? '').split(',');
    const parsed = raw.map((entry) => String(entry).trim().toUpperCase()).filter(Boolean);

    return parsed.length ? [...new Set(parsed)] : fallback;
  }

  /**
   * As parseList, but for keywords matched as substrings.
   *
   * Drops a trailing "S" so the merchant can type either "Charms" or "Charm" and
   * get the same behaviour. Without this, matching is one-sided: a plural keyword
   * ("CHARMS") fails against a singular product type ("Charm"), while a singular
   * keyword matches both. Not applied to vendors, which are compared exactly.
   */
  parseKeywords(value, fallback) {
    const singular = (keyword) => (keyword.length > 3 && keyword.endsWith('S') ? keyword.slice(0, -1) : keyword);

    return this.parseList(value, fallback).map(singular);
  }

  /**
   * The charm link comes from a theme setting and is injected into innerHTML,
   * so escape it rather than trusting the stored value.
   */
  escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  getProductVendorFromElement(element) {
    if (!element) return '';
    return (element.getAttribute('data-product-vendor') || '').trim().toUpperCase();
  }

  getProductTypeFromElement(element) {
    if (!element) return '';
    return (element.getAttribute('data-product-type') || '').trim().toUpperCase();
  }

  getTotalBraceletQuantity(nominationBracelets) {
    return nominationBracelets.reduce((total, item) => total + item.quantity, 0);
  }

  getTotalCharmQuantity(charms) {
    return charms.reduce((total, item) => total + item.quantity, 0);
  }

  /**
   * Check if cart drawer is currently open
   */
  isCartDrawerOpen() {
    const cartDrawer = document.querySelector('#CartDrawer, cart-drawer, .cart-drawer');
    if (!cartDrawer) return false;

    return (
      cartDrawer.classList.contains('is-empty') === false &&
      (cartDrawer.classList.contains('active') ||
        cartDrawer.classList.contains('open') ||
        cartDrawer.offsetParent !== null)
    );
  }

  /**
   * Restore body overflow to fix frozen screen when cart drawer closes
   */
  /**
   * Display tags on cart items for debugging
   */
  displayTagsOnCartItems(cartItems) {
    cartItems.forEach((item) => {
      const tags = this.getProductTagsFromElement(item.element);
      const vendor = this.getProductVendorFromElement(item.element);
      const type = this.getProductTypeFromElement(item.element);

      // Remove existing debug tag display
      const existingDebug = item.element.querySelector('.debug-tags-display');
      if (existingDebug) {
        existingDebug.remove();
      }

      // Create new debug tag display
      if (tags.length > 0 || vendor || type) {
        const tagDisplay = document.createElement('div');
        tagDisplay.className = 'debug-tags-display';
        tagDisplay.style.cssText = `
          background: #f0f0f0;
          border: 1px solid #ddd;
          padding: 5px 8px;
          margin-top: 5px;
          font-size: 11px;
          border-radius: 3px;
          color: #333;
        `;

        const badge = (label, color) =>
          `<span style="background: ${color}; color: white; padding: 2px 6px; border-radius: 2px; margin-right: 4px; display: inline-block; font-size: 10px;">${label}</span>`;

        // Color code the tags
        const tagBadges = tags
          .map((tag) => {
            const normalised = tag.trim().toUpperCase();
            let color = '#999';
            if (this.matchers.brandTags.includes(normalised)) color = '#2563eb';
            if (this.matchesKeyword(this.matchers.charmKeywords, normalised)) color = '#059669';

            return badge(tag, color);
          })
          .join('');

        const vendorBadge = vendor
          ? badge(`vendor: ${vendor}`, this.matchers.brandVendors.includes(vendor) ? '#2563eb' : '#999')
          : '';
        const typeBadge = type ? badge(`type: ${type}`, '#b45309') : '';

        tagDisplay.innerHTML = `<strong>${vendorBadge}${typeBadge}</strong><br><strong>Tags:</strong> ${tagBadges}`;

        // Insert after product title
        const titleElement = item.element.querySelector('.cart-item__name, .cart-item__title');
        if (titleElement) {
          titleElement.parentNode.insertBefore(tagDisplay, titleElement.nextSibling);
        } else {
          // Fallback: append to item details
          const detailsElement = item.element.querySelector('.cart-item__details, .cart-item__info');
          if (detailsElement) {
            detailsElement.appendChild(tagDisplay);
          }
        }
      }
    });
  }

  /**
   * Show the warning and block express checkout.
   *
   * Deliberately has no "already shown" early return. renderWarning() creates the
   * element only when missing and rewrites its text only when it changes, so
   * calling this repeatedly is cheap AND self-healing when Dawn re-renders the
   * cart section and destroys the warning. A boolean guard cannot do both: the
   * previous version set the flag and then called clearAllWarnings(), which reset
   * it again - so the guard never guarded, and every pass rewrote the warning and
   * flipped every CTA's inline display twice.
   */
  showWarnings(nominationBracelets, totalBraceletQuantity, totalCharmQuantity) {
    this.warningsShouldBeShown = true;

    this.showCartWarning(totalBraceletQuantity, totalCharmQuantity);
    this.hideExpressCheckout();
  }

  showItemWarning(bracelet, totalBraceletQuantity, totalCharmQuantity) {
    const warningElement = this.createWarningElement();
    warningElement.innerHTML = this.warningMessage;

    // Insert warning after the product details
    const detailsCell = bracelet.element.querySelector('.cart-item__details');
    if (detailsCell) {
      detailsCell.appendChild(warningElement);
    }
  }

  showCartWarning(totalBraceletQuantity, totalCharmQuantity) {
    // Show warning in cart footer as first full-width item
    this.showCartPageWarning();
    this.showCartDrawerWarning();
  }

  /**
   * Create (or reuse) the warning element inside `container`, and show it.
   *
   * The element is a live region. Without one this is a silent failure for
   * assistive tech: a validation error appears and the customer's only route to
   * checkout is removed, with nothing announced. `role="alert"` carries assertive
   * politeness; `aria-live`/`aria-atomic` are set explicitly for older AT.
   *
   * The text is only rewritten when it changes or when the element is being
   * revealed after being hidden. Rewriting `innerHTML` on every validation pass
   * destroys and recreates the charm link (losing focus if the customer is on it),
   * and a reused region that never changes may not be re-announced at all.
   */
  renderWarning(container, id) {
    let warningElement = document.getElementById(id);

    if (!warningElement) {
      warningElement = document.createElement('div');
      warningElement.id = id;
      warningElement.className = 'nomination-cart-warning';
      warningElement.setAttribute('role', 'alert');
      warningElement.setAttribute('aria-live', 'assertive');
      warningElement.setAttribute('aria-atomic', 'true');
      // Focusable programmatically (not in the tab order) so focus can be moved
      // here if it was sitting on a control we are about to disable.
      warningElement.setAttribute('tabindex', '-1');
      container.insertBefore(warningElement, container.firstChild);
    }

    const wasHidden = warningElement.style.display === 'none';
    if (wasHidden || warningElement.nominationMessage !== this.warningMessage) {
      warningElement.innerHTML = this.warningMessage;
      warningElement.nominationMessage = this.warningMessage;
    }

    // Write only on change. Assigning an identical value still rewrites the
    // attribute and emits a mutation record, which is what made a steady-state
    // cart churn the DOM on every validation pass.
    if (warningElement.style.display !== 'block') {
      warningElement.style.display = 'block';
    }
    if (warningElement.getAttribute('data-nomination-warning') !== 'true') {
      warningElement.setAttribute('data-nomination-warning', 'true');
    }

    return warningElement;
  }

  showCartPageWarning() {
    // Only show cart page warning if we're on the main cart page, not in drawer
    if (document.querySelector('#CartDrawer[open], .cart-drawer[open], .drawer[open]')) {
      return;
    }

    // Find cart footer on main cart page - prioritize .cart__footer
    const cartFooter = document.querySelector('.cart__footer');

    // Make sure we're not targeting cart drawer elements
    if (cartFooter && cartFooter.closest('#CartDrawer, .cart-drawer, .drawer')) {
      return;
    }

    if (cartFooter) {
      this.renderWarning(cartFooter, 'nomination-cart-warning');
    } else {
      // Fallback: try to find other cart containers
      const fallbackContainer = document.querySelector(
        ' .cart__dynamic-checkout-buttons, .cart__checkout, .cart__totals, .cart__footer-wrapper, .cart__content, #main-cart-items, .cart, .cart-page',
      );
      if (fallbackContainer) {
        this.renderWarning(fallbackContainer, 'nomination-cart-warning');
      }
    }
  }

  showCartDrawerWarning() {
    // Find cart drawer footer
    const drawerFooter = document.querySelector('.cart-drawer__footer');

    if (drawerFooter) {
      this.renderWarning(drawerFooter, 'nomination-cart-drawer-warning');
    } else {
      // Fallback: try to find other drawer containers
      const fallbackContainer = document.querySelector(
        '#CartDrawer-CartItems, .drawer__footer, .cart-drawer, .drawer__content, #CartDrawer, .drawer__inner',
      );
      if (fallbackContainer) {
        this.renderWarning(fallbackContainer, 'nomination-cart-drawer-warning');
      }
    }
  }

  createWarningElement() {
    const warning = document.createElement('div');
    warning.className = 'nomination-item-warning';

    // Add warning icon
    const icon = document.createElement('span');
    icon.innerHTML = '⚠️';
    warning.appendChild(icon);

    return warning;
  }

  clearAllWarnings() {
    // Remove item-level warnings
    document.querySelectorAll('.nomination-item-warning').forEach((warning) => {
      warning.remove();
    });

    // Hide cart page warning
    const cartWarning = document.getElementById('nomination-cart-warning');
    if (cartWarning) {
      cartWarning.style.display = 'none';
    }

    // Hide cart drawer warning
    const drawerWarning = document.getElementById('nomination-cart-drawer-warning');
    if (drawerWarning) {
      drawerWarning.style.display = 'none';
    }

    // Note: We're keeping debug tags display for testing - don't remove them here

    // Reset warning flag
    this.warningsShouldBeShown = false;

    // Show cart footer when clearing warnings
    this.showExpressCheckout();
  }

  // Method to force clear warnings (used when validation passes)
  forceClearWarnings() {
    this.warningsShouldBeShown = false;
    this.clearAllWarnings();
  }

  clearItemWarning(bracelet) {
    // Clear warning for specific bracelet
    const detailsCell = bracelet.element.querySelector('.cart-item__details');
    if (detailsCell) {
      const existingWarning = detailsCell.querySelector('.nomination-item-warning');
      if (existingWarning) {
        existingWarning.remove();
      }
    }
  }

  /**
   * Block EVERY checkout control on the page.
   *
   * Note the querySelectorAll: the cart page and the cart drawer both render a
   * checkout button, and the drawer's comes first in the DOM. A `querySelector`
   * that stopped at the first match therefore disabled the drawer's button and
   * left the cart page's "Proceed to checkout" fully clickable - the customer
   * could see the warning and check out anyway. That went unnoticed while this
   * feature also hid the buttons outright.
   */
  disableCheckout() {
    const checkoutButtons = new Set([
      ...document.querySelectorAll('button[name="checkout"], #checkout, .cart__checkout-button, button[form="cart"]'),
      ...document.querySelectorAll('.additional-checkout-buttons button, .cart__dynamic-checkout-buttons button'),
    ]);

    const drawerCheckoutButton = document.getElementById('CartDrawer-Checkout');
    if (drawerCheckoutButton) checkoutButtons.add(drawerCheckoutButton);

    checkoutButtons.forEach((button) => {
      // Point each button at the warning that is actually next to it.
      const inDrawer = button.closest('#CartDrawer, cart-drawer, .cart-drawer, .drawer');
      this.markCheckoutBlocked(button, inDrawer ? 'nomination-cart-drawer-warning' : 'nomination-cart-warning');
    });
  }

  /**
   * Disable a checkout control AND say why, programmatically.
   *
   * `disabled` alone tells assistive tech only that the control is unavailable,
   * never that a rule was broken or how to satisfy it. aria-describedby points at
   * the warning so the reason is reachable from the control itself.
   */
  markCheckoutBlocked(button, describedById) {
    const hadFocus = document.activeElement === button;

    button.disabled = true;
    button.setAttribute('data-nomination-disabled', 'true');
    button.setAttribute('aria-disabled', 'true');

    const warning = document.getElementById(describedById);
    if (warning) {
      button.setAttribute('aria-describedby', describedById);

      // A disabled control drops out of the tab order, so a keyboard user focused
      // on it would be dumped back to the top of the document with no idea why.
      // Move them to the explanation instead.
      if (hadFocus) warning.focus();
    }
  }

  /**
   * Re-enable only the controls THIS feature disabled.
   *
   * Keyed off our own marker rather than re-querying the checkout selectors,
   * because those also match buttons Dawn disables for its own reasons (empty
   * cart, an in-flight cart update, a line-item error) and the wallet buttons in
   * .cart__dynamic-checkout-buttons, which the theme does not own. Blanket
   * `disabled = false` would clobber all of them.
   */
  enableCheckout() {
    const ourWarningIds = ['nomination-cart-warning', 'nomination-cart-drawer-warning'];

    document.querySelectorAll('[data-nomination-disabled="true"]').forEach((button) => {
      button.disabled = false;
      button.removeAttribute('data-nomination-disabled');
      button.removeAttribute('aria-disabled');

      // Only clear the description if it is the one we set.
      if (ourWarningIds.includes(button.getAttribute('aria-describedby'))) {
        button.removeAttribute('aria-describedby');
      }
    });
  }

  /**
   * Hide the express/wallet checkout buttons only.
   *
   * The primary checkout button is deliberately left in place, disabled and
   * described by the warning (see markCheckoutBlocked). Removing it made the
   * blocked state unexplained: the affordance simply vanished, and because the
   * selector was unscoped it took the cart drawer's button with it - contrary to
   * this file's own comment.
   *
   * The express container still has to be hidden rather than disabled. Shop Pay /
   * PayPal / Google Pay render in a cross-origin iframe, so their controls are not
   * reachable as `button` elements and cannot be disabled individually; hiding the
   * wrapper is the only way to close that checkout path.
   */
  hideExpressCheckout() {
    document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons').forEach((container) => {
      container.style.display = 'none';
    });
  }

  /** Restore the express/wallet checkout buttons hidden by hideExpressCheckout. */
  showExpressCheckout() {
    document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons').forEach((container) => {
      container.style.display = '';
    });
  }

  // Removed aggressive validation - now only validates on specific events

  // Force cart refresh to ensure DOM is updated
  forceCartRefresh() {
    // Use Cart Manager if available
    if (this.cartManager) {
      this.cartManager
        .forceRefresh()
        .then(() => {
          this.validateCart();
        })
        .catch((error) => {
          this.validateCart();
        });
      return;
    }

    // Clear any existing cart cache
    if (window.cart && typeof window.cart.clearCache === 'function') {
      window.cart.clearCache();
    }

    // Force fetch fresh cart data with cache busting
    const cacheBuster = Date.now();
    fetch(`/cart.js?t=${cacheBuster}`, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    })
      .then((response) => response.json())
      .then((cartData) => {
        // Trigger validation after getting fresh data
        setTimeout(() => {
          this.validateCart();
        }, 100);
      })
      .catch((error) => {
        // Fallback to regular validation
        this.validateCart();
      });

    // Try to trigger cart refresh via Shopify's cart system
    if (window.cart && typeof window.cart.refresh === 'function') {
      window.cart.refresh();
    }

    // Try to trigger cart update event
    if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }

    // Force validation after refresh
    setTimeout(() => {
      this.validateCart();
    }, 200);
  }

  // Cleanup method
  destroy() {
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
    }
    if (this.removeValidationInterval) {
      clearInterval(this.removeValidationInterval);
    }
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
    }

    // Clean up Cart Manager subscriptions
    if (this.unsubscribeCartUpdated) this.unsubscribeCartUpdated();
    if (this.unsubscribeCartApiCall) this.unsubscribeCartApiCall();
    if (this.unsubscribeQuantityChange) this.unsubscribeQuantityChange();
    if (this.unsubscribeItemRemove) this.unsubscribeItemRemove();
    if (this.unsubscribeDomChanged) this.unsubscribeDomChanged();
    if (this.unsubscribeFormSubmit) this.unsubscribeFormSubmit();
  }
}

// Initialize the validation when the script loads
const nominationValidation = new NominationCartValidation();

// Expose debugging method to global scope for testing
window.nominationValidation = {
  enableDebug: () => {
    nominationValidation.debug = true;
    console.log('Nomination cart validation debugging enabled');
  },
  disableDebug: () => {
    nominationValidation.debug = false;
    console.log('Nomination cart validation debugging disabled');
  },
  showTags: () => {
    nominationValidation.showDebugTags = true;
    const cartItems = nominationValidation.getCartItems();
    nominationValidation.displayTagsOnCartItems(cartItems);
    console.log('Debug tags enabled and displayed on cart items');
  },
  hideTags: () => {
    nominationValidation.showDebugTags = false;
    document.querySelectorAll('.debug-tags-display').forEach((tag) => tag.remove());
    console.log('Debug tags disabled and hidden');
  },
  validate: () => {
    nominationValidation.validateCart();
  },
  forceValidate: () => {
    console.log('Forcing validation...');
    nominationValidation.validateCart();
  },
  testCartChanges: () => {
    console.log('Testing cart changes...');
    console.log('Current cart items:', nominationValidation.getCartItems());
    console.log(
      'Nomination bracelets:',
      nominationValidation.getNominationBracelets(nominationValidation.getCartItems()),
    );
    console.log('Charms:', nominationValidation.getCharms(nominationValidation.getCartItems()));
    nominationValidation.validateCart();
  },
  debugValidation: () => {
    console.log('=== DEBUGGING VALIDATION ===');
    const cartItems = nominationValidation.getCartItems();
    console.log('Total cart items found:', cartItems.length);

    cartItems.forEach((item, index) => {
      console.log(`Item ${index + 1}:`, {
        title: item.title,
        quantity: item.quantity,
        vendor: nominationValidation.getProductVendorFromElement(item.element),
        type: nominationValidation.getProductTypeFromElement(item.element),
        tags: nominationValidation.getProductTagsFromElement(item.element),
        isNominationBracelet: nominationValidation.isNominationBracelet(item.element),
        isNominationCharm: nominationValidation.isNominationCharm(item.element),
      });
    });

    const nominationBracelets = nominationValidation.getNominationBracelets(cartItems);
    const charms = nominationValidation.getCharms(cartItems);

    console.log('Nomination bracelets found:', nominationBracelets.length);
    console.log('Charms found:', charms.length);

    if (nominationBracelets.length > 0) {
      const totalBraceletQuantity = nominationValidation.getTotalBraceletQuantity(nominationBracelets);
      const totalCharmQuantity = nominationValidation.getTotalCharmQuantity(charms);
      console.log('Total bracelet quantity:', totalBraceletQuantity);
      console.log('Total charm quantity:', totalCharmQuantity);
      console.log('Should show warnings:', totalCharmQuantity < totalBraceletQuantity);
    }

    nominationValidation.validateCart();
  },
  testRemoveAction: () => {
    console.log('Testing remove action validation...');
    console.log('Current state before remove:', {
      cartItems: nominationValidation.getCartItems().length,
      nominationBracelets: nominationValidation.getNominationBracelets(nominationValidation.getCartItems()).length,
      charms: nominationValidation.getCharms(nominationValidation.getCartItems()).length,
    });
    nominationValidation.validateCart();
  },
  testRemoveButtons: () => {
    console.log('Testing remove button detection...');
    const removeButtons = document.querySelectorAll('cart-remove-button');
    console.log('Found remove buttons:', removeButtons.length);
    removeButtons.forEach((button, index) => {
      console.log(`Remove button ${index + 1}:`, {
        id: button.id,
        hasListener: button.hasAttribute('data-nomination-listener'),
        innerHTML: button.innerHTML,
      });
    });
  },
  forceRefreshValidation: () => {
    console.log('Force refreshing validation...');
    // Clear all warnings first
    nominationValidation.clearAllWarnings();
    // Force validation multiple times
    nominationValidation.validateCart();
    setTimeout(() => nominationValidation.validateCart(), 100);
    setTimeout(() => nominationValidation.validateCart(), 500);
    setTimeout(() => nominationValidation.validateCart(), 1000);
  },
  waitAndValidate: (delay = 1000) => {
    console.log(`Waiting ${delay}ms then validating...`);
    setTimeout(() => {
      console.log('Delayed validation running');
      nominationValidation.validateCart();
    }, delay);
  },
  forceCartRefresh: () => {
    console.log('Manually forcing cart refresh...');
    nominationValidation.forceCartRefresh();
  },
  getCartItems: () => {
    return nominationValidation.getCartItems();
  },
  getNominationBracelets: () => {
    const cartItems = nominationValidation.getCartItems();
    return nominationValidation.getNominationBracelets(cartItems);
  },
  getCharms: () => {
    const cartItems = nominationValidation.getCartItems();
    return nominationValidation.getCharms(cartItems);
  },
  checkCheckoutButtons: () => {
    const buttons = {
      mainCart: document.querySelector('button[name="checkout"]'),
      mainCartById: document.getElementById('checkout'),
      mainCartByClass: document.querySelector('.cart__checkout-button'),
      mainCartByForm: document.querySelector('button[form="cart"]'),
      drawer: document.getElementById('CartDrawer-Checkout'),
      additional: document.querySelectorAll(
        '.additional-checkout-buttons button, .cart__dynamic-checkout-buttons button',
      ),
    };

    console.log('Checkout buttons found:', {
      mainCart: !!buttons.mainCart,
      mainCartById: !!buttons.mainCartById,
      mainCartByClass: !!buttons.mainCartByClass,
      mainCartByForm: !!buttons.mainCartByForm,
      drawer: !!buttons.drawer,
      additional: buttons.additional.length,
      allButtons: Object.values(buttons).filter((btn) => btn && (btn.length === undefined ? true : btn.length > 0)),
    });

    return buttons;
  },
};
